// The standalone path view's "specialized data structure": the chain from a curriculum
// entry to a target song, expressed as a MapLayout (the path's own lattice nodes,
// repositioned in a line at runtime). Fed to the SAME DiscoveryMap as the full map
// (openPath) — so focus, the rail, nav, and pan/zoom all work identically; only the
// data differs. Pure. See docs/superpowers/specs/2026-06-13-bring-your-own-song-design.md.

import type { CatalogEntry } from "../songs/catalog";
import { isChordAtom } from "../songs/chord-atoms";
import type { LayoutEdge, LayoutNode, MapLayout } from "../songs/map-layout";
import { pathTo } from "../songs/pathfind";
import { buildSongLattice, type LatticeNode, type SongLattice } from "../songs/song-lattice";
import { planUpload } from "../songs/upload-plan";
import type { NoteEvent } from "../types";

const STEP_X = 200; // horizontal spacing between rungs
const ROW_Y = 70; // single row

/** Lay a path (lattice nodes) out as a linear MapLayout, carrying the lattice edges. */
export function buildPathLayout(path: readonly LatticeNode[], lattice: SongLattice): MapLayout {
  const edgeBy = new Map(lattice.edges.map((e) => [`${e.from} ${e.to}`, e]));
  const nodes: LayoutNode[] = path.map((n, i) => ({
    ...n,
    isRoot: i === 0,
    x: STEP_X * (i + 1),
    y: ROW_Y,
  }));
  const edges: LayoutEdge[] = [];
  for (let i = 0; i + 1 < path.length; i++) {
    const from = path[i];
    const to = path[i + 1];
    if (!from || !to) continue;
    const e = edgeBy.get(`${from.id} ${to.id}`);
    if (e) {
      edges.push({ ...e }); // no spline points → straight line
    } else {
      const prev = new Set(from.requirements);
      const adds = to.requirements.filter((r) => !prev.has(r));
      edges.push({
        from: from.id,
        to: to.id,
        adds,
        delta: adds,
        label: adds.join(", "),
        kind: adds.length > 0 && adds.every(isChordAtom) ? "chord" : "technique",
      });
    }
  }
  return { nodes, edges };
}

interface SongPath {
  layout: MapLayout;
  /** Optional note, e.g. out-of-vocabulary chords an upload needs. */
  note?: string;
}

/**
 * Plan the path layout for a song: a catalog song (incl. LOCKED ones) routes to its
 * lattice node; an uploaded file is analyzed by `planUpload`. Returns null when no route
 * exists or the lattice can't be built.
 */
export function planSongPath(
  songId: string,
  catalog: readonly CatalogEntry[],
  upload?: { notes: readonly NoteEvent[]; bars: readonly number[] },
): SongPath | null {
  try {
    const lattice = buildSongLattice(catalog);
    const entry = catalog.find((e) => e.id === songId);
    let path: ReturnType<typeof pathTo> = null;
    let note: string | undefined;
    if (entry) {
      const node = lattice.nodes.find((n) => n.songIds.includes(entry.id));
      path = node ? pathTo(lattice, node.id) : null;
    } else if (upload) {
      const drilled = new Set(catalog.flatMap((e) => e.teaches ?? []));
      const plan = planUpload(upload.notes, upload.bars, lattice, drilled);
      path = plan.path;
      if (plan.beyond.length > 0) note = `Beyond what we teach yet: ${plan.beyond.join(", ")}`;
    }
    if (!path) return null;
    return { layout: buildPathLayout(path, lattice), note };
  } catch {
    return null;
  }
}
